import { useMemo, useState } from "react";
import { ArrowDown, FolderOpen, Link2, Plus, Search, TestTube2, UploadCloud } from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import ExperimentCard from "./components/ExperimentCard";
import { experimentCategories, experimentsData, experimentStatusList } from "@/mocks/experiments";
import type { Experiment } from "@/mocks/experiments";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { workflowsData, workflowStatusList, workflowSubjects } from "@/mocks/workflows";
import WorkflowCard from "@/pages/workflows/components/WorkflowCard";
import { useNavigate } from "react-router-dom";

const mainTabs = ["智能体", "工作流", "MCP", "Skill", "模型", "数据库"] as const;

type MainTab = (typeof mainTabs)[number];

export default function ExperimentsPage() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeMain, setActiveMain] = useState<MainTab>("智能体");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [newMenuOpen, setNewMenuOpen] = useState(false);

  const filteredExperiments = useMemo(() => {
    return experimentsData.filter((exp) => {
      const matchSearch =
        searchQuery === "" ||
        exp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exp.category.toLowerCase().includes(searchQuery.toLowerCase());
      const matchCategory = selectedCategory === "all" ? true : exp.category === selectedCategory;
      const matchStatus = selectedStatus === "all" ? true : exp.status === selectedStatus;
      return matchSearch && matchCategory && matchStatus;
    });
  }, [searchQuery, selectedCategory, selectedStatus]);

  const filteredWorkflows = useMemo(() => {
    return workflowsData.filter((wf) => {
      const matchSearch =
        searchQuery === "" ||
        wf.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wf.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchSubject = selectedCategory === "all" ? true : wf.subject === selectedCategory;
      const matchStatus = selectedStatus === "all" ? true : wf.status === selectedStatus;
      return matchSearch && matchSubject && matchStatus;
    });
  }, [searchQuery, selectedCategory, selectedStatus]);

  const isWorkflowTab = activeMain === "工作流";
  const categoryOptions = isWorkflowTab ? [...workflowSubjects] : experimentCategories;
  const statusOptions = isWorkflowTab ? [...workflowStatusList] : experimentStatusList;

  return (
    <div className="flex h-screen overflow-hidden bg-white text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-transparent">
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1100px] space-y-5">
            <div className="flex items-center justify-between gap-4 rounded-3xl border border-white/60 bg-white py-3 backdrop-blur">
              <div className="flex items-center gap-8 overflow-x-auto whitespace-nowrap px-2">
                {mainTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveMain(tab)}
                    className={`text-[18px] transition-colors ${
                      activeMain === tab ? "font-semibold text-foreground" : "font-normal text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">学科</span>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-[170px]">
                      <SelectValue placeholder="全部学科" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部学科</SelectItem>
                      {categoryOptions.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">状态</span>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="全部状态" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部状态</SelectItem>
                      {statusOptions.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex h-10 w-[260px] items-center gap-2 rounded-lg border border-input bg-background px-3 shadow-sm">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="请输入关键词"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>

                <div className="relative">
                  <button
                    onClick={() => setNewMenuOpen((prev) => !prev)}
                    className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
                  >
                    <Plus className="h-4 w-4" />
                    新建
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  {newMenuOpen && (
                    <div className="absolute right-0 top-full z-20 mt-2 min-w-44 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg">
                      {[
                        {
                          icon: FolderOpen,
                          label: isWorkflowTab ? "新建工作流" : "新建智能体",
                          onClick: () => {
                            if (isWorkflowTab) navigate("/workflows/create");
                          },
                        },
                        { icon: UploadCloud, label: "导入内容" },
                        { icon: Link2, label: "添加链接" },
                      ].map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.label}
                            onClick={() => {
                              item.onClick?.();
                              setNewMenuOpen(false);
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                          >
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {(isWorkflowTab ? filteredWorkflows : filteredExperiments).length === 0 ? (
              <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card text-center shadow-sm">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                  <TestTube2 className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight">{isWorkflowTab ? "未找到匹配的工作流" : "未找到匹配的实验项目"}</h3>
                <p className="mt-2 text-sm text-muted-foreground">尝试调整筛选条件或搜索关键词。</p>
              </div>
            ) : (
              <div className="grid gap-4 pb-4 md:grid-cols-2 xl:grid-cols-4">
                {isWorkflowTab
                  ? filteredWorkflows.map((wf) => (
                      <WorkflowCard key={wf.id} item={wf} onOpen={(id) => navigate(`/workflows/create?workflowId=${encodeURIComponent(id)}`)} />
                    ))
                  : filteredExperiments.map((exp) => <ExperimentCard key={exp.id} experiment={exp as Experiment} />)}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
